import React, { useState, useEffect, useRef, useCallback } from "react";
import { ListGroup, Spinner } from "react-bootstrap";
import { PlayFill, Send } from "react-bootstrap-icons";
import SubTitle from "components/SubTitle";
import { getFormattedTime } from "components/Messages/utils";
import { useTranslation } from "react-i18next";
import TextareaAutosize from "react-textarea-autosize";
import { LocalStorageKey, useGetUserData } from "common/helpers/userData";
import {
  useGetChatMessagesQuery,
  useMarkChatAsSeenMutation,
  useSendMessageMutation,
} from "features/api/messages/messagesApiSlice";
import {
  ERROR_MESSAGE,
  UserRole,
} from "components/Reports/reportAssets/assets";
import { getAdjustedISOString } from "pages/ControlCenter/ContentManagement/contentManagementUtils/contentManagementUtils";
import { toast } from "react-toastify";
import {
  chatDetailsDataType,
  chatProps,
  markChatAsSeenDataType,
  messageDataType,
} from "../../types";
import Message from "./Message";
import "../../styles.scss";

const Chat = ({
  setIsOpen,
  isReportContainerOpen,
  selectedChat,
}: chatProps) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<messageDataType[]>([]);
  const [isEndOfList, setIsEndOfList] = useState(false);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [messageText, setMessageText] = useState("");
  const messagesContainerRef = useRef(null);
  const controllerName = useGetUserData(LocalStorageKey.UserName);
  const isControlCenter =
    useGetUserData(LocalStorageKey.SelectedRole)?.value ===
    UserRole.CONTROLCENTER;
  const heightForMessagesContainer = isControlCenter ? "83vh" : "74vh";

  const [sendMessage] = useSendMessageMutation();
  const [MarkChatAsSeenQuery] = useMarkChatAsSeenMutation();

  const {
    data: messagesList,
    isLoading,
    isFetching,
  }: {
    data: chatDetailsDataType;
    isLoading: boolean;
    isFetching: boolean;
  } = useGetChatMessagesQuery(
    {
      page: pageNumber,
      reportId: selectedChat?.id,
    },
    {
      skip: !selectedChat?.id,
    }
  );

  // updating the messages list and the state that say if we get the all messages
  useEffect(() => {
    setMessages(messagesList?.messages);
    setIsEndOfList(messagesList?.isEndOfList);

    const messagesContainer = messagesContainerRef.current;
    if (messagesContainer.scrollTop === 0) {
      messagesContainer.scrollTop = (messagesContainer.scrollHeight * 2) / 100;
    }
  }, [messagesList]);

  const handleMarkChatAsSeen = useCallback(
    async (id: number) => {
      const chatToSend: markChatAsSeenDataType = {
        chatId: id,
      };
      try {
        await MarkChatAsSeenQuery(chatToSend).unwrap();
      } catch (e) {
        console.error(e);
      }
    },
    [MarkChatAsSeenQuery]
  );

  // resetting the states
  useEffect(() => {
    setShouldScrollToBottom(true);
    setPageNumber(1);
    setMessageText("");
    if (selectedChat && selectedChat.id) {
      handleMarkChatAsSeen(selectedChat.id);
    }
  }, [selectedChat, handleMarkChatAsSeen]);

  const handleScroll = useCallback(() => {
    const messagesContainer = messagesContainerRef.current;
    // Check if the user has reached the top of the page.
    if (
      !isEndOfList &&
      messagesContainer &&
      messagesContainer.scrollTop === 0
    ) {
      // If the user reached the top and data is not already loading, load more data.
      if (!isFetching) {
        setPageNumber((prev) => prev + 1);
      }
    }
  }, [isEndOfList, isFetching, setPageNumber]);

  useEffect(() => {
    // Add a scroll event listener to the messagesContainer to detect when the user has scrolled.
    const messagesContainer = messagesContainerRef.current;

    if (messagesContainer) {
      messagesContainer.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (messagesContainer) {
        messagesContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [messages, handleScroll]);

  useEffect(() => {
    // Scroll to the bottom of the chat container when we select a chat
    const messagesContainer = messagesContainerRef.current;
    if (messagesList && messagesContainer && shouldScrollToBottom) {
      setShouldScrollToBottom(false);
      setTimeout(() => {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }, 50);
    }
  }, [messagesList, shouldScrollToBottom]);

  const handleSubmitMessage = async () => {
    const messageTextFormatted: messageDataType = {
      id: selectedChat.id,
      date: getAdjustedISOString(new Date()),
      sentByController: true,
      senderName: controllerName,
      content: messageText,
      sentByControlCenter: isControlCenter,
    };
    try {
      await sendMessage(messageTextFormatted).unwrap();
      setMessageText("");
      setShouldScrollToBottom(true);
    } catch (e) {
      console.error(e);
      toast.error(ERROR_MESSAGE);
    }
  };

  return (
    <div
      className="messagesScreenContainer"
      style={{
        flex: isReportContainerOpen ? 1 : 2.5,
      }}
    >
      {isLoading ? (
        <div className="loader-container">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          <SubTitle className="title">
            {messagesList?.volunteerFullName}
          </SubTitle>

          <div
            className="messagesListContainer"
            style={{
              maxHeight: heightForMessagesContainer,
              height: heightForMessagesContainer,
            }}
            ref={messagesContainerRef}
          >
            <ListGroup className="list-group">
              <div
                className="loading"
                style={{
                  visibility: isFetching ? "visible" : "hidden",
                }}
              >
                {t("loadingMore")}
              </div>
              {messagesList?.reportShot && (
                <ListGroup.Item
                  className="videoSnapshot"
                  onClick={() => setIsOpen(true)}
                >
                  <div className="videoContainer">
                    <img
                      src={messagesList?.reportShot}
                      alt="videoSnapshot"
                      width={360}
                      height={240}
                    />
                    <PlayFill
                      fill="white"
                      className="playIcon"
                      width={60}
                      height={60}
                    />
                  </div>
                  <div className="time">
                    {getFormattedTime(messagesList?.firstMessageDate)}
                  </div>
                </ListGroup.Item>
              )}
              {messages?.map((message) => (
                <Message key={message?.id} message={message} />
              ))}
            </ListGroup>
          </div>
          {messages?.length > 0 && (
            <div className="formContainer">
              <TextareaAutosize
                maxRows={5}
                placeholder={t("inputMessagePlaceholder")}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
              />
              <Send
                type="submit"
                className="iconButton"
                width={24}
                height={24}
                onClick={handleSubmitMessage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Chat;
